import React, { useState, useEffect } from 'react';
import { preloadImages } from './utils/imagePreloader';

// Safe wrapper component that gracefully handles shader loading
const SafeWarpShader: React.FC = () => {
  const [shaderLoaded, setShaderLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [WarpComponent, setWarpComponent] = useState<React.ComponentType<any> | null>(null);
  const [fadeOut, setFadeOut] = useState(false);

  // Load shader dynamically
  useEffect(() => {
    const loadShader = async () => {
      try {
        const { Warp } = await import("@paper-design/shaders-react");
        setWarpComponent(() => Warp);
        setShaderLoaded(true);
      } catch (error) {
        console.warn('Shader failed to load, using fallback:', error);
        setShaderLoaded(false);
      }
    };

    loadShader();
  }, []);

  // Preload heavy images in background
  useEffect(() => {
    const loadImages = async () => {
      const imageUrls: string[] = [
        '/home.png',
      ];

      try {
        await preloadImages(imageUrls);
        console.log('Images preloaded successfully');
      } catch (error) {
        console.warn('Some images failed to preload:', error);
      }
    };

    loadImages();
  }, []);

  // 10-second loading timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => setLoading(false), 800);
    }, 10000); // Exactly 10 seconds

    return () => clearTimeout(timer);
  }, []);

  if (!loading) {
    return (
      <div className="fixed inset-0 z-50">
        {/* Background - either shader or fallback */}
        <div className="absolute inset-0">
          {shaderLoaded && WarpComponent ? (
            <WarpComponent
              style={{ height: "100%", width: "100%" }}
              proportion={0.45}
              softness={1}
              distortion={0.25}
              swirl={0.8}
              swirlIterations={10}
              shape="checks"
              shapeScale={0.1}
              scale={1}
              rotation={0}
              speed={1}
              colors={["#693EFE", "hsl(210, 90%, 40%)", "#D3FE3E", "hsl(230, 75%, 50%)"]}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 animate-gradient-xy" />
          )}
        </div>

        {/* Main content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center px-8">
          <div className="max-w-4xl w-full text-center space-y-8">
            <h1 className="text-white text-7xl md:text-9xl font-logo font-bold text-balance animate-fade-in drop-shadow-2xl">
              Fleait
            </h1>
            <p className="text-gray-300 text-3xl md:text-5xl font-sans font-bold leading-relaxed max-w-4xl mx-auto animate-fade-in-delay">
              New York City
            </p>
            {/* <div className="space-y-4 animate-fade-in-delay-2">
              <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-8 py-3 rounded-full transition-all duration-300">
                Enter Experience
              </button>
            </div> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 transition-opacity duration-800 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
      {/* Background - either shader or fallback */}
      <div className="absolute inset-0">
        {shaderLoaded && WarpComponent ? (
          <WarpComponent
            style={{ height: "100%", width: "100%" }}
            proportion={0.45}
            softness={1}
            distortion={0.25}
            swirl={0.8}
            swirlIterations={10}
            shape="checks"
            shapeScale={0.1}
            scale={1}
            rotation={0}
            speed={1}
            colors={["#693EFE", "hsl(210, 90%, 40%)", "#D3FE3E", "hsl(230, 75%, 50%)"]}
          />
        ) : (
          // Fallback gradient background - blue gradient
          <div className="w-full h-full bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 animate-gradient-xy" />
        )}
      </div>

      {/* Loading content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-8">
        <div className="max-w-4xl w-full text-center space-y-8 flex-1 flex flex-col justify-center">
          <h1 className="text-white text-7xl md:text-9xl font-logo font-bold text-balance animate-fade-in drop-shadow-2xl">
            Fleait
          </h1>

          <p className="text-gray-300 text-3xl md:text-5xl font-sans font-bold leading-relaxed max-w-4xl mx-auto animate-fade-in-delay">
            New York City
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafeWarpShader;